import { Suspense, useCallback, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet } from 'react-router-dom';
import ErrorFallback from '@/pages/ErrorFallback/ErrorFallback';
import Footer from '@/components/base/Footer/Footer';
import Header from '@/components/base/Header/Header';
import useChatbotStore from '@/components/Chatbot/store/useChatbotStore';
import CookieConsentBanner from '@/components/consent/CookieConsentBanner/CookieConsentBanner';
import { useAuthContext } from '@/context/AuthContext';
import SharedViewDialog from '@/components/filter/SharedViewDialog/SharedViewDialog';
import useApplySharedView from '@/hooks/useApplySharedView';
import useApplyDefaultFilter from '@/hooks/useApplyDefaultFilter';
import useApplyExternalFilters from '@/hooks/useApplyExternalFilters';
import { Filter } from '@cubejs-client/core';
import useUserProfile from '@/hooks/useUserProfile';
import AIButton from '@/storybook/components/AIButton/AIButton';
import ErrorBoundaryMorePrecised from '@/storybook/components/ErrorBoundaryMorePrecised/ErrorBoundaryMorePrecised';
import LoadingSpinner from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import styles from './Layout.module.css';
import PageBanner from './PageBanner/PageBanner';

const Layout = () => {
  const user = useUserProfile();
  const { userInfo } = useAuthContext();
  const { isLoading: isApplyingSharedView, sharedViewDialogProps } = useApplySharedView();
  const { isLoading: isApplyingDefaultFilter } = useApplyDefaultFilter();
  const { isLoading: isApplyingExternalFilters } = useApplyExternalFilters();
  const [tierFilterForHeader, setTierFilterForHeader] = useState<Filter>();
  const [ppocFilterForHeader, setPpocFilterForHeader] = useState<Filter>();
  const isDrawerOpen = useChatbotStore(state => state.isDrawerOpen);
  const setDrawerOpen = useChatbotStore(state => state.setDrawerOpen);

  const handleAIButtonClick = useCallback(() => {
    setDrawerOpen(true);
  }, [setDrawerOpen]);

  const context = useMemo(
    () => ({
      setTierFilterForHeader,
      setPpocFilterForHeader,
      ppocFilterForHeader,
      tierFilterForHeader,
    }),
    [ppocFilterForHeader, tierFilterForHeader],
  );

  const shouldShowAIButton = Boolean(userInfo);

  const showFilterBar = user?.disclaimerRead;

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <CookieConsentBanner />
      <Header tierFilter={tierFilterForHeader} ppocFilter={ppocFilterForHeader} />
      <main className={styles.main}>
        <PageBanner />
        {isApplyingSharedView || isApplyingDefaultFilter || isApplyingExternalFilters ? (
          <LoadingSpinner />
        ) : (
          <Suspense fallback={<LoadingSpinner />}>
            <ErrorBoundaryMorePrecised>
              <Outlet context={context} />
            </ErrorBoundaryMorePrecised>
          </Suspense>
        )}
      </main>
      {showFilterBar && <Footer />}
      {sharedViewDialogProps && <SharedViewDialog {...sharedViewDialogProps} />}
      {shouldShowAIButton && !isDrawerOpen && <AIButton onClick={handleAIButtonClick} />}
    </ErrorBoundary>
  );
};

export default Layout;
